import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { articles } from "@/mocks/articles";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const related = useMemo(() => {
    if (!article) return [];
    const same = articles.filter((a) => a.id !== article.id && a.category === article.category);
    const others = articles.filter((a) => a.id !== article.id && a.category !== article.category);
    return [...same, ...others].slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <section className="container-site flex min-h-[60vh] flex-col items-center justify-center py-24 text-center">
          <h1 className="text-2xl font-bold">未找到该篇文章</h1>
          <p className="mt-3 text-sm text-foreground-600">这篇解读可能已被调整，去修炼体系页看看其他内容吧。</p>
          <Link
            to="/xiulian"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回修炼体系
          </Link>
        </section>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.cover,
    datePublished: article.date,
    author: { "@type": "Organization", name: "天才医仙漫画修炼体系站" },
  };

  return (
    <>
      <Navbar />
      <BackToTop />

      <section className="bg-primary-700 pt-24 pb-10 md:pt-28 md:pb-12">
        <div className="container-site">
          <nav aria-label="面包屑" className="text-xs text-primary-300">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/xiulian" className="hover:text-background-50">修炼体系</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-accent-300">{article.category}</li>
            </ol>
          </nav>
          <h1 className="mt-5 max-w-3xl text-2xl font-bold leading-snug text-background-50 md:text-3xl">{article.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-primary-200">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-price-tag-3-line" aria-hidden="true"></i>
              {article.tag}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {article.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" aria-hidden="true"></i>
              {article.readTime}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="container-site grid gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="overflow-hidden rounded-lg border border-background-200/80">
              <img src={article.cover} alt={article.title} className="h-full w-full object-cover object-top" />
            </div>

            <div className="mt-8 space-y-6">
              {article.content.map((para, i) => (
                <p key={i} className="text-[15px] leading-8 text-foreground-800">
                  {para}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200/80 pt-6">
              <span className="text-sm text-foreground-500">相关标签：</span>
              {[article.category, article.tag, article.level].map((t) => (
                <span key={t} className="rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-700">
                  {t}
                </span>
              ))}
            </div>
          </article>

          <aside className="space-y-8">
            <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-6">
              <h2 className="font-heading text-lg font-bold">本文导读</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
              <ul className="mt-4 space-y-2 text-sm text-foreground-700">
                <li className="flex items-center gap-2"><i className="ri-leaf-fill text-primary-600" aria-hidden="true"></i>{article.category}体系</li>
                <li className="flex items-center gap-2"><i className="ri-sparkling-fill text-accent-600" aria-hidden="true"></i>{article.level}</li>
                <li className="flex items-center gap-2"><i className="ri-bookmark-line text-primary-600" aria-hidden="true"></i>{article.readTime}</li>
              </ul>
            </div>

            <div className="rounded-lg bg-primary-700 p-6 text-background-50">
              <h2 className="font-heading text-lg font-bold">还没看过体系总览？</h2>
              <p className="mt-2 text-sm leading-relaxed text-primary-100">
                先读六大境界的总览文章，快速掌握医仙之路的升级脉络，再回来深入细读。
              </p>
              <Link
                to="/article/yi-xian-zhi-lu"
                className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-300 hover:text-accent-200"
              >
                查看六大境界 <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="container-site">
          <h2 className="text-xl font-bold md:text-2xl">相关推荐</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-transform hover:-translate-y-1"
              >
                <div className="relative h-36 w-full overflow-hidden">
                  <img
                    src={a.cover}
                    alt={a.title}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">
                    {a.category}
                  </span>
                  <h3 className="mt-2.5 font-heading text-base font-bold leading-snug group-hover:text-primary-700">
                    {a.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <Footer />
    </>
  );
}